/*
 * eslint-disable no-console -- this file IS the local development wrapper: its
 * lifecycle logging (queue drained → mapping → creative rendered) is what makes
 * the integration verifiable without a whitelisted domain. Mock mode is opt-in
 * (aditude_mock_mode), so none of this runs on a real tenant. The production
 * path in aditudeManager.ts deliberately logs nothing but errors.
 */
/* eslint-disable no-console */
import { AditudeRuntimeConfig, AditudeSlotMapping, TudeApi } from "./aditudeTypes";

const DEFAULT_AUCTION_DELAY_MS = 900;
const QUEUE_DRAIN_DELAY_MS = 300;

/**
 * HTML-escape a value before it is interpolated into an innerHTML string or an
 * attribute (srcdoc). baseDivId/divId originate from author-controlled Ad Slot
 * config (base_div_id_x_str), so a raw `'`, `"`, `<` or `>` could otherwise
 * break out of the surrounding tag/attribute and inject markup (stored XSS,
 * CWE-79). Escapes `&` first so already-escaped entities are not double-encoded.
 */
const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

/**
 * Local fake of the Aditude Cloud Wrapper, byte-compatible with the adapter:
 * it drains the `tude.cmd` queue like the real script does (then monkey-patches
 * `cmd.push` so later pushes run immediately) and answers
 * `refreshAdsViaDivMappings` by injecting a labelled placeholder iframe after a
 * simulated auction delay. Lets the whole DXP-side flow run on localhost —
 * real fill needs an Aditude-whitelisted domain.
 */
export function installMockTude(tude: TudeApi, config: AditudeRuntimeConfig = {}): void {
  if (tude?.__mockInstalled) return;
  tude.__mockInstalled = true;
  tude.__mockState = {};

  tude.setPageTargeting = (targeting) => {
    if (tude.__mockState) tude.__mockState.targeting = targeting;
    console.info("[mock tude] setPageTargeting", targeting);
  };

  tude.setAdUnitPath = (adUnitPath) => {
    if (tude.__mockState) tude.__mockState.adUnitPath = adUnitPath;
    console.info("[mock tude] setAdUnitPath", adUnitPath);
  };

  tude.refreshAdsViaDivMappings = (mappings) => {
    mappings?.forEach((mapping) => {
      const el = document.getElementById(mapping?.divId);
      if (!el) {
        console.warn("[mock tude] div not found (mapped before mount?):", mapping?.divId);
        return;
      }
      renderMockAuction(el, mapping, tude, config);
    });
  };

  const drain = (fn: () => void) => {
    try {
      fn();
    } catch (e) {
      console.error("[mock tude] cmd error", e);
    }
  };

  // Simulate the async script load: drain the queue on a later tick, exactly
  // like the real wrapper, so pre-load `cmd.push` ordering is exercised.
  setTimeout(() => {
    const pending = tude.cmd.splice(0);
    pending.forEach(drain);
    tude.cmd.push = ((fn: () => void) => {
      drain(fn);
      return tude.cmd.length;
    }) as typeof tude.cmd.push;
    console.info("[mock tude] wrapper 'loaded', queue drained (%d cmds)", pending.length);
  }, QUEUE_DRAIN_DELAY_MS);
}

function renderMockAuction(
  el: HTMLElement,
  mapping: AditudeSlotMapping,
  tude: TudeApi,
  config: AditudeRuntimeConfig,
): void {
  el.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100%;min-height:90px;background:#f0f2f5;color:#556;font:13px monospace;border:1px dashed #bbc">
      Auction running… (mock Prebid → GAM)
    </div>`;

  setTimeout(() => {
    const iu =
      tude.__mockState?.adUnitPath ||
      `/${config?.gamNetworkCode || "0000000"}/mock/${mapping?.baseDivId}`;
    // iu (author-set adUnitPath) and baseDivId/divId (author-controlled Ad Slot
    // config) are NEVER interpolated into the iframe `srcdoc`: a srcdoc value is
    // re-parsed as its own HTML document, so entities inside it decode a second
    // time and single-escaping there would still execute injected markup. The
    // iframe stays a fixed banner; the dynamic labels render as escaped text in
    // the PARENT element (single-level escaping is correct in element content).
    const safeIu = escapeHtml(iu);
    const safeBaseDivId = escapeHtml(mapping?.baseDivId);
    const safeDivId = escapeHtml(mapping?.divId);
    el.innerHTML = `
      <iframe
        title="3rd party ad content (mock)"
        scrolling="no"
        style="display:block;width:100%;height:100%;min-height:90px;border:0"
        srcdoc='
          <body style="margin:0;font-family:monospace;background:repeating-linear-gradient(45deg,#e8f4e8,#e8f4e8 12px,#d7ead7 12px,#d7ead7 24px);display:flex;align-items:center;justify-content:center;height:100vh">
            <div style="text-align:center;color:#2c4a2c">
              <div style="font-size:15px;font-weight:bold">MOCK AD — Aditude wrapper simulation</div>
            </div>
          </body>'
      ></iframe>
      <div style="font:11px monospace;color:#2c4a2c;text-align:center;padding:2px">
        iu=${safeIu}_0 · baseDivId=${safeBaseDivId} → divId=${safeDivId}
      </div>`;
    console.info("[mock tude] rendered creative into #%s (iu=%s_0)", mapping?.divId, iu);
  }, config?.mockAuctionDelayMs ?? DEFAULT_AUCTION_DELAY_MS);
}
